import { useFormBehaviors } from 'hooks';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { createContext, FC, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { API_PATCH_biddingConfiguration } from 'requests/PATCH_biddingConfiguration';
import { toastService } from 'services';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { useTranslation } from 'hooks/useTranslation';
import isUserPrimarySelector from 'store/selectors/userSelectors/isUserPrimarySelector';
import { useShiftDistributionLocationDataContext } from 'pages/Settings/modals/ShiftDistributionRulesTieringModal/views/ShiftDistributionLocation.data.context';

interface IBiddingConfigurationFormSchema {
  employmentStatuses: string[];
  biddingOvertimeDefault: boolean;
}

const ShiftBiddingDetailsFormContext = createContext<
  UseFormBehaviors<IBiddingConfigurationFormSchema, {}> & {
    isEditMode: boolean;
    setIsEditMode: (value: boolean) => void;
  }
>(
  {} as UseFormBehaviors<IBiddingConfigurationFormSchema, {}> & {
    isEditMode: boolean;
    setIsEditMode: (value: boolean) => void;
  },
);

export const ShiftBiddingDetailsFormProvider: FC = ({ children }) => {
  const { pathname } = useLocation();
  const locationId = pathname.split('/')[3];
  const { apiRequest } = useAPIActions();
  const { t } = useTranslation();
  const { biddingConfiguration, fetch } = useShiftDistributionLocationDataContext();
  const canEditDefaultOvertime = useSelector(isUserPrimarySelector);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleSubmitBiddingConfiguration = useCallback(
    async (formValues) => {
      await apiRequest(
        API_PATCH_biddingConfiguration(locationId, {
          employment_statuses: formValues.employmentStatuses,
          ...(canEditDefaultOvertime && {
            bidding_overtime_default: formValues.biddingOvertimeDefault,
          }),
        }),
        {
          onSuccess: () => {
            fetch && fetch();
            setIsEditMode(false);
            toastService.success(t('location_settings:BIDDING_CONFIGURATION_SAVED'));
          },
          onError: (e) =>
            handleGenericErrorMessage(e, t('errors:FAILED_TO_UPDATE_BIDDING_CONFIGURATION')),
        },
      );
    },
    [apiRequest, locationId, fetch, t, canEditDefaultOvertime],
  );

  const form = useFormBehaviors<IBiddingConfigurationFormSchema, {}>({
    initialState: {
      employmentStatuses: biddingConfiguration?.conflictEmploymentStatuses || [],
      biddingOvertimeDefault: biddingConfiguration?.biddingOvertimeDefault ?? true,
    },
    type: 'EDIT',
    isDirtyCheckEnabled: false,
    onSubmit: handleSubmitBiddingConfiguration,
    validations: {},
  });

  const syncFormWithBiddingConfiguration = useCallback(() => {
    if (!biddingConfiguration) return;
    form.onChange([
      {
        target: {
          name: 'employmentStatuses',
          value: biddingConfiguration.conflictEmploymentStatuses || [],
        },
      },
      {
        target: {
          name: 'biddingOvertimeDefault',
          value: biddingConfiguration.biddingOvertimeDefault ?? true,
        },
      },
    ]);
  }, [biddingConfiguration]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    syncFormWithBiddingConfiguration();
  }, [syncFormWithBiddingConfiguration]);

  const handleSetEditMode = (value: boolean) => {
    setIsEditMode(value);
    if (!value) syncFormWithBiddingConfiguration();
  };

  const value = useMemo(
    () => ({
      ...form,
      isEditMode,
      setIsEditMode: handleSetEditMode,
    }),
    [form, isEditMode, biddingConfiguration],
  );

  return (
    <ShiftBiddingDetailsFormContext.Provider value={value}>
      {children}
    </ShiftBiddingDetailsFormContext.Provider>
  );
};

export const useShiftBiddingDetailsFormContext = () => useContext(ShiftBiddingDetailsFormContext);
